import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { Plan } from "@headerhawk/core";

export type OnboardingStep = "org" | "tier" | "site" | "setup";

const STEP_ORDER: OnboardingStep[] = ["org", "tier", "site", "setup"];

interface OnboardingState {
  currentStep: OnboardingStep;
  completedSteps: OnboardingStep[];
  selectedTier: Plan | null;
  createdSiteId: string | null;

  // Actions
  setStep: (step: OnboardingStep) => void;
  completeStep: (step: OnboardingStep) => void;
  setSelectedTier: (tier: Plan) => void;
  setCreatedSiteId: (siteId: string) => void;
  reset: () => void;
  isStepCompleted: (step: OnboardingStep) => boolean;
  canAccessStep: (step: OnboardingStep) => boolean;
  getNextStep: (currentStep: OnboardingStep) => OnboardingStep | null;
  getPreviousStep: (currentStep: OnboardingStep) => OnboardingStep | null;
}

export const useOnboardingStore = create<OnboardingState>()(
  persist(
    (set, get) => ({
      currentStep: "org",
      completedSteps: [],
      selectedTier: null,
      createdSiteId: null,

      setStep: (step) => set({ currentStep: step }),

      completeStep: (step) => {
        const { completedSteps } = get();
        if (!completedSteps.includes(step)) {
          set({ completedSteps: [...completedSteps, step] });
        }
      },

      setSelectedTier: (tier) => set({ selectedTier: tier }),
      setCreatedSiteId: (siteId) => set({ createdSiteId: siteId }),

      reset: () =>
        set({
          currentStep: "org",
          completedSteps: [],
          selectedTier: null,
          createdSiteId: null,
        }),

      isStepCompleted: (step) => get().completedSteps.includes(step),

      canAccessStep: (step) => {
        const { completedSteps } = get();
        const stepIndex = STEP_ORDER.indexOf(step);
        if (stepIndex === 0) return true;
        if (stepIndex === -1) return false;
        const previousStep = STEP_ORDER[stepIndex - 1];
        if (!previousStep) return false;
        return completedSteps.includes(previousStep);
      },

      getNextStep: (currentStep) => {
        const currentIndex = STEP_ORDER.indexOf(currentStep);
        if (currentIndex === -1 || currentIndex >= STEP_ORDER.length - 1) {
          return null;
        }
        return STEP_ORDER[currentIndex + 1] ?? null;
      },

      getPreviousStep: (currentStep) => {
        const currentIndex = STEP_ORDER.indexOf(currentStep);
        if (currentIndex <= 0) {
          return null;
        }
        return STEP_ORDER[currentIndex - 1] ?? null;
      },
    }),
    {
      name: "onboarding-state",
    },
  ),
);

export { STEP_ORDER };
